import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import {
  chargeRuleSource,
  chargeRuleToOption,
  guestSource,
  pitchSource,
} from "@/lib/autocompleteSources";
import {
  CONTRACT_TYPES,
  pitchUsageTypeForContractType,
  type ContractTypeValue,
} from "@/lib/contractType";
import {
  billingIntervalFromCategory,
  billingIntervalLabel,
  billingPeriodAmountLabel,
  chargeRuleForPitchCategory,
  contractEndForStart,
  contractGrossFromChargeRule,
} from "@/lib/contractBilling";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { normalizeRentalPeriod, rentalPeriodPriceLabel } from "@/lib/rentalPeriod";
import { usePitchCategories } from "@/lib/usePitchCategories";
import { ContractChoice, ContractFormSection } from "@/pages/contracts/contractDisplay";
import type {
  Article,
  ArticlePrice,
  ChargeRule,
  Contract,
  ContractCreatePayload,
  Guest,
  Pitch,
} from "@/types/camping";

const EMPTY_FORM: ContractCreatePayload = {
  guest_id: "",
  contract_type: "permanent",
  pitch_id: "",
  start_date: new Date().toISOString().slice(0, 10),
  notice_period_days: 90,
  billing_interval: "monthly",
  auto_renew: false,
  billing_period_amount_gross: "",
  charge_rule_id: "",
  notes: "",
  create_booking: true,
};

export interface CreateContractModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateContractModal({ open, onClose, onCreated }: CreateContractModalProps) {
  const [form, setForm] = useState<ContractCreatePayload>(EMPTY_FORM);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [chargeRules, setChargeRules] = useState<ChargeRule[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [articlePrices, setArticlePrices] = useState<ArticlePrice[]>([]);
  const [chargeRuleArticleLoading, setChargeRuleArticleLoading] = useState(false);
  const [guestOption, setGuestOption] = useState<AutocompleteOption<Guest> | null>(null);
  const [pitchOption, setPitchOption] = useState<AutocompleteOption<Pitch> | null>(null);
  const [chargeRuleOption, setChargeRuleOption] = useState<AutocompleteOption<ChargeRule> | null>(
    null,
  );
  const [endDate, setEndDate] = useState("");
  const [endDateManual, setEndDateManual] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openContracts, setOpenContracts] = useState<Contract[]>([]);
  const { categories } = usePitchCategories(open);

  const computedEndDate = useMemo(
    () => contractEndForStart(form.start_date, form.billing_interval),
    [form.start_date, form.billing_interval],
  );

  const replacedContract = useMemo(() => {
    if (!form.pitch_id || !form.start_date) return null;
    if (openContracts.some((contract) => contract.pitch_id === form.pitch_id && contract.status === "active")) {
      return null;
    }
    const newEnd = endDate || computedEndDate || "9999-12-31";
    return (
      openContracts.find((contract) => {
        if (contract.pitch_id !== form.pitch_id || contract.status !== "notice") return false;
        const oldEnd = contract.end_date ?? "9999-12-31";
        return form.start_date <= oldEnd && contract.start_date <= newEnd;
      }) ?? null
    );
  }, [openContracts, form.pitch_id, form.start_date, endDate, computedEndDate]);

  const availablePitches = useMemo(() => {
    const usageType = pitchUsageTypeForContractType(form.contract_type);
    return pitches.filter((p) => p.usage_type === usageType);
  }, [form.contract_type, pitches]);

  const activeChargeRules = useMemo(
    () => chargeRules.filter((rule) => rule.is_active),
    [chargeRules],
  );

  const loadGuestOptions = useCallback((query: string) => guestSource(query), []);
  const loadPitchOptions = useCallback(
    (query: string) => pitchSource(availablePitches, query),
    [availablePitches],
  );
  const loadChargeRuleOptions = useCallback(
    (query: string) => chargeRuleSource(activeChargeRules, query),
    [activeChargeRules],
  );

  const loadData = useCallback(() => {
    api.get<Pitch[]>("/api/v1/pitches").then(setPitches).catch(() => setPitches([]));
    api
      .get<ChargeRule[]>("/api/v1/charge-rules")
      .then(setChargeRules)
      .catch(() => setChargeRules([]));
    api
      .get<Article[]>(`/api/v1/articles?as_of=${encodeURIComponent(new Date().toISOString().slice(0, 10))}`)
      .then(setArticles)
      .catch(() => setArticles([]));
    api
      .get<Contract[]>("/api/v1/contracts")
      .then(setOpenContracts)
      .catch(() => setOpenContracts([]));
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setGuestOption(null);
    setPitchOption(null);
    setChargeRuleOption(null);
    setEndDateManual(false);
    setError(null);
    setSubmitting(false);
    loadData();
  }, [open, loadData]);

  useEffect(() => {
    if (!endDateManual) {
      setEndDate(computedEndDate);
    }
  }, [computedEndDate, endDateManual]);

  useEffect(() => {
    setEndDateManual(false);
  }, [form.start_date, form.billing_interval]);

  useEffect(() => {
    if (!form.pitch_id) return;
    const stillValid = availablePitches.some((p) => p.id === form.pitch_id);
    if (!stillValid) {
      setForm((f) => ({ ...f, pitch_id: "" }));
      setPitchOption(null);
    }
  }, [availablePitches, form.pitch_id]);

  const selectedPitch = useMemo(
    () => pitches.find((pitch) => pitch.id === form.pitch_id) ?? pitchOption?.value ?? null,
    [form.pitch_id, pitchOption, pitches],
  );

  const selectedCategory = useMemo(() => {
    const categoryId = selectedPitch?.category_id;
    if (!categoryId) return null;
    return categories.find((category) => category.id === categoryId) ?? null;
  }, [categories, selectedPitch]);

  const categoryInterval = billingIntervalFromCategory(selectedCategory);
  const categoryName = selectedCategory?.name ?? selectedPitch?.category_name ?? null;

  useEffect(() => {
    const next = categoryInterval ?? "monthly";
    setForm((current) =>
      current.billing_interval === next ? current : { ...current, billing_interval: next },
    );
  }, [categoryInterval]);

  const selectedChargeRule = useMemo(
    () => activeChargeRules.find((rule) => rule.id === form.charge_rule_id) ?? null,
    [activeChargeRules, form.charge_rule_id],
  );

  const selectedArticle = useMemo(() => {
    if (!selectedChargeRule) return null;
    return articles.find((article) => article.id === selectedChargeRule.article_id) ?? null;
  }, [articles, selectedChargeRule]);

  const computedGrossFromChargeRule = useMemo(() => {
    if (!selectedArticle || !endDate) return null;
    return contractGrossFromChargeRule(
      selectedArticle,
      articlePrices,
      form.start_date,
      endDate,
      form.billing_interval,
    );
  }, [selectedArticle, articlePrices, form.start_date, form.billing_interval, endDate]);

  useEffect(() => {
    if (!form.pitch_id) return;
    const pitch = pitches.find((p) => p.id === form.pitch_id);
    if (!pitch?.category_id) return;
    const rule = chargeRuleForPitchCategory(activeChargeRules, pitch.category_id);
    if (!rule || rule.id === form.charge_rule_id) return;
    const fullRule = activeChargeRules.find((r) => r.id === rule.id);
    if (!fullRule) return;
    setForm((f) => ({
      ...f,
      charge_rule_id: fullRule.id,
      billing_period_amount_gross: "",
    }));
    setChargeRuleOption(chargeRuleToOption(fullRule));
  }, [form.pitch_id, form.charge_rule_id, pitches, activeChargeRules]);

  useEffect(() => {
    if (!selectedChargeRule) {
      setArticlePrices([]);
      return;
    }
    let cancelled = false;
    setChargeRuleArticleLoading(true);
    api
      .get<ArticlePrice[]>(`/api/v1/articles/${selectedChargeRule.article_id}/prices`)
      .then((prices) => {
        if (!cancelled) setArticlePrices(prices);
      })
      .catch(() => {
        if (!cancelled) setArticlePrices([]);
      })
      .finally(() => {
        if (!cancelled) setChargeRuleArticleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedChargeRule]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.guest_id || !form.pitch_id) {
      setError("Gast und Stellplatz sind erforderlich.");
      return;
    }
    if (!form.billing_period_amount_gross && !form.charge_rule_id) {
      setError("Pauschale (brutto) oder Buchungszuordnung angeben.");
      return;
    }
    if (endDate && endDate < form.start_date) {
      setError("Vertragsende darf nicht vor dem Vertragsbeginn liegen.");
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        guest_id: form.guest_id,
        contract_type: form.contract_type,
        pitch_id: form.pitch_id,
        start_date: form.start_date,
        end_date: endDate || null,
        notice_period_days: form.notice_period_days,
        billing_interval: form.billing_interval,
        auto_renew: form.auto_renew,
        create_booking: form.create_booking,
        notes: form.notes.trim() || null,
      };
      if (form.billing_period_amount_gross) {
        payload.billing_period_amount_gross = form.billing_period_amount_gross;
      }
      if (form.charge_rule_id) payload.charge_rule_id = form.charge_rule_id;
      await api.post<Contract>("/api/v1/contracts", payload);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neuer Vertrag"
      description="Gast, Platz und den ersten Abrechnungszeitraum festlegen"
      size="lg"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" form="contract-create-form" loading={submitting}>
            Vertrag anlegen
          </Button>
        </div>
      }
    >
      <form id="contract-create-form" onSubmit={(e) => void onSubmit(e)} className="space-y-5">
        <ContractFormSection title="Gast und Platz">
          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Vertragsart</span>
            <select
              className="eh-input w-full"
              value={form.contract_type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  contract_type: e.target.value as ContractTypeValue,
                  pitch_id: "",
                }))
              }
              required
            >
              {CONTRACT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-label-tertiary">
              {CONTRACT_TYPES.find((t) => t.value === form.contract_type)?.description}
            </p>
          </label>

          <LockedAutocomplete
            label="Gast"
            required
            placeholder="Name, E-Mail oder Telefon suchen…"
            minChars={2}
            value={guestOption}
            loadOptions={loadGuestOptions}
            onChange={(option) => {
              setGuestOption(option);
              setForm((f) => ({ ...f, guest_id: option?.value.id ?? "" }));
            }}
          />

          <LockedAutocomplete
            label="Stellplatz"
            required
            placeholder="Platznummer oder Bezeichnung suchen…"
            hint={`${availablePitches.length} Stellplätze für diese Vertragsart verfügbar`}
            value={pitchOption}
            loadOptions={loadPitchOptions}
            onChange={(option) => {
              setPitchOption(option);
              setForm((f) => ({ ...f, pitch_id: option?.value.id ?? "" }));
            }}
          />
        </ContractFormSection>

        <ContractFormSection title="Laufzeit">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Beginn</span>
              <input
                type="date"
                className="eh-input w-full"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                required
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-label-secondary">Ende des ersten Zeitraums</span>
              <input
                type="date"
                className="eh-input w-full tabular-nums"
                value={endDate}
                min={form.start_date}
                onChange={(e) => {
                  setEndDateManual(true);
                  setEndDate(e.target.value);
                }}
                required
              />
              <p className="text-xs text-label-tertiary">
                Vorschlag: {computedEndDate}
                {endDateManual && endDate !== computedEndDate ? " · manuell angepasst" : ""}
              </p>
            </label>
          </div>

          <label className="block space-y-1 text-sm sm:max-w-xs">
            <span className="text-label-secondary">Kündigungsfrist (Tage)</span>
            <input
              type="number"
              min={0}
              className="eh-input w-full"
              value={form.notice_period_days}
              onChange={(e) =>
                setForm((f) => ({ ...f, notice_period_days: Number(e.target.value) || 0 }))
              }
            />
          </label>

          <ContractChoice
            checked={form.auto_renew}
            onChange={(checked) => setForm((f) => ({ ...f, auto_renew: checked }))}
            title="Automatisch verlängern"
            hint="Nach dem Vertragsende um einen Abrechnungszeitraum."
          />
        </ContractFormSection>

        <ContractFormSection
          title="Abrechnung"
          description="Das Intervall kommt aus der Stellplatzkategorie des Platzes."
        >
          <div className="rounded-lg border border-separator bg-card-elevated px-3 py-2.5">
            <p className="text-xs text-label-tertiary">Abrechnungsintervall</p>
            <p className="mt-0.5 text-sm font-medium text-label">
              {billingIntervalLabel(categoryInterval ?? form.billing_interval)}
            </p>
            <p className="mt-0.5 text-xs text-label-secondary">
              {categoryInterval && categoryName
                ? `Aus der Stellplatzkategorie ${categoryName}.`
                : selectedCategory &&
                    (normalizeRentalPeriod(selectedCategory.rental_period) === "night" ||
                      normalizeRentalPeriod(selectedCategory.rental_period) === "week")
                  ? `${categoryName ?? "Diese Kategorie"} wird ${rentalPeriodPriceLabel(selectedCategory.rental_period)} vermietet. Verträge bleiben monatlich.`
                  : selectedPitch
                    ? categoryName
                      ? `Die Kategorie ${categoryName} hat noch kein Vertragsintervall. Bis dahin gilt monatlich.`
                      : "Diesem Platz ist keine Stellplatzkategorie zugeordnet. Bis dahin gilt monatlich."
                    : "Wird übernommen, sobald ein Stellplatz gewählt ist."}
            </p>
          </div>

          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">
              {billingPeriodAmountLabel(form.billing_interval)}
            </span>
            <input
              type="text"
              inputMode="decimal"
              className="eh-input w-full"
              placeholder={
                form.charge_rule_id
                  ? "Optional – Preis kommt aus der Zuordnung"
                  : "z. B. 374,50"
              }
              value={form.billing_period_amount_gross}
              onChange={(e) =>
                setForm((f) => ({ ...f, billing_period_amount_gross: e.target.value }))
              }
            />
            {form.charge_rule_id ? (
              <p className="text-xs text-label-secondary">
                {chargeRuleArticleLoading
                  ? "Preis wird berechnet…"
                  : computedGrossFromChargeRule != null
                    ? `Berechnet: ${formatCurrency(computedGrossFromChargeRule)} brutto für diesen Zeitraum`
                    : selectedArticle
                      ? "Kein gültiger Artikelpreis für den gewählten Zeitraum."
                      : "Artikel der Zuordnung wird geladen…"}
              </p>
            ) : (
              <p className="text-xs text-label-tertiary">
                Pauschale oder Zuordnung – eines von beiden reicht.
              </p>
            )}
          </label>

          <LockedAutocomplete
            label="Preis aus Zuordnung"
            placeholder="Regel oder Artikel suchen…"
            hint="Wird beim Stellplatz vorgeschlagen, wenn eine passende Regel existiert."
            value={chargeRuleOption}
            loadOptions={loadChargeRuleOptions}
            onChange={(option) => {
              setChargeRuleOption(option);
              setForm((f) => ({
                ...f,
                charge_rule_id: option?.value.id ?? "",
                billing_period_amount_gross: option?.value.id ? "" : f.billing_period_amount_gross,
              }));
            }}
          />
        </ContractFormSection>

        <ContractFormSection title="Buchung und Notizen">
          <ContractChoice
            checked={form.create_booking}
            onChange={(checked) => setForm((f) => ({ ...f, create_booking: checked }))}
            title="Laufende Buchung anlegen"
            hint="Verknüpft den Vertrag mit einer Buchung auf diesem Platz."
          />

          <label className="block space-y-1 text-sm">
            <span className="text-label-secondary">Notizen</span>
            <textarea
              className="eh-input w-full min-h-[4rem]"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Interne Hinweise zum Vertrag"
            />
          </label>
        </ContractFormSection>

        {replacedContract ? (
          <p className="text-sm text-label-secondary">
            Auf diesem Platz läuft ein gekündigter Vertrag
            {replacedContract.guest_name ? ` von ${replacedContract.guest_name}` : ""} noch
            {replacedContract.end_date
              ? ` bis ${formatIsoDateShort(replacedContract.end_date)}`
              : ""}
            . Der neue Vertrag kann trotzdem angelegt werden. Die tagesgenaue Gutschrift lösen Sie
            danach am gekündigten Vertrag aus.
          </p>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </form>
    </Modal>
  );
}
